/**
 * Conversations API layer
 * Handles fetching and managing conversation data from the backend
 */

import { queryOptions } from '@tanstack/react-query';
import { apiClient } from './base/client';
import { ApiError } from './base/errors';
import { normalizeGenieSpaceNames } from '../utils/genie';

export type DigitalMaintenanceStatus = 'processing' | 'done';

/**
 * Conversation interface matching backend response
 * Note: Backend has typo 'accesed' instead of 'accessed'
 */
export interface Conversation {
  conversationID: string;
  message_id: string | null;
  conversationTitle: string;
  profile: string;
  category: Record<string, unknown>;
  date_last_accesed: string; // Note: Backend typo preserved for compatibility
}

/**
 * Conversation API parameters
 */
export interface ConversationParams {
  profile?: string;
}

/**
 * Enhanced chat message interface with message-level file support
 */
export interface ChatMessage {
  user: string;
  bot: string;
  message_id: string;
  digital_maintenance_status?: DigitalMaintenanceStatus;
  filterCategory?: string;
  top?: number;
  temperature?: number;
  promptTemplate?: string;
  gptVersion?: string;
  nativeSearch?: boolean;
  showPreviewFeatures?: boolean;
  use_files?: boolean;
  tools?: Array<{name: string}>;
  appVersion?: string;
  agent_classification?: string[];

  // Message-level file association
  uploadedFiles?: string[];  // Files uploaded with this specific message
  upload_session_id?: string;  // Upload session reference for traceability

  genie_space_names?: string[];
  genie_conversation_id?: string;
  genie_message_id?: string;

  // Streamed tool call data persisted from WebSocket
  toolCalls?: Array<{ id?: string; agent: string; tool?: string; args?: Record<string, unknown>; content: string; status: string; startedAt: number; completedAt?: number }>;
}

/**
 * Conversation detail response
 */
export interface ConversationDetailResponse {
  conversationID: string;
  conversationHistory: ChatMessage[];

  // Remove conversation-level file tracking
  // uploadedFiles: string[];  // No longer needed

  genie_conversation_ids?: Record<string, unknown>;
  genie_space_names?: string[];
  genie_conversation_id?: string;
  foundry_rag_conversation_ids?: Record<string, string>;
  websearch_conversation_id?: string;

  // File processing status (async upload)
  file_processing_status?: 'processing' | 'complete' | 'partial' | 'failed';
}

/**
 * Rename conversation request payload
 */
export interface RenameConversationRequest {
  userConversations: Array<{
    conversationID: string;
    conversationTitle: string;
    category: Record<string, unknown>;
  }>;
}

/**
 * Rename conversation response
 */
export interface RenameConversationResponse {
  message: string;
  updated_ids: string[];
}

/**
 * Single conversation rename parameters
 */
export interface RenameConversationParams {
  conversationId: string;
  newTitle: string;
}

/**
 * Delete conversation request payload
 */
export interface DeleteConversationRequest {
  conversationID: string[];
}

/**
 * Delete conversation response
 */
export interface DeleteConversationResponse {
  statistics: {
    total_time: number;
  };
}

/**
 * Branch conversation request payload
 */
export interface BranchConversationParams {
  newConversationId: string;
  newConversationTitle: string;
  conversationHistory: ChatMessage[];
  profile?: string;
  category?: Record<string, unknown>;
  genie_conversation_ids?: Record<string, unknown>;
  genie_space_names?: string[];
  genie_conversation_id?: string;
  foundry_rag_conversation_ids?: Record<string, string>;
  websearch_conversation_id?: string;
}

/**
 * Branch conversation response
 */
export interface BranchConversationResponse {
  message: string;
  updated_ids: string[];
}

/**
 * Single conversation delete parameters (UI interface)
 */
export interface DeleteConversationParams {
  conversationId: string;
}

/**
 * Batch conversation delete parameters (future extensibility)
 */
export interface BatchDeleteConversationParams {
  conversationIds: string[];
}




export interface UpdateConversationRequest {
  conversationID: string;
}

export interface UpdateConversationResponse {
  message: string;
}

/**
 * Custom error class for conversation-specific errors
 */
export class ConversationApiError extends ApiError {
  public readonly name: string = 'ConversationApiError';

  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    Object.setPrototypeOf(this, ConversationApiError.prototype);
  }
}

/**
 * Fetch conversations from the backend
 */
export async function fetchConversations(
  params: ConversationParams = {}
): Promise<Conversation[]> {
  try {
    // Build query parameters
    const searchParams = new URLSearchParams();
    if (params.profile) {
      searchParams.append('profile', params.profile);
    }

    const queryString = searchParams.toString();
    const endpoint = `/userconversations${queryString ? `?${queryString}` : ''}`;

    // Make API request
    const response = await apiClient.get<Conversation[]>(endpoint);

    // Validate response
    if (!Array.isArray(response)) {
      throw new ConversationApiError(
        'Invalid response format: expected array',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    // Validate conversation objects
    response.forEach((conversation, index) => {
      if (!conversation.conversationID || !conversation.conversationTitle) {
        throw new ConversationApiError(
          `Invalid conversation at index ${index}: missing required fields`,
          undefined,
          'INVALID_CONVERSATION'
        );
      }
    });

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ConversationApiError(
      `Failed to fetch conversations: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'FETCH_FAILED'
    );
  }
}

/**
 * Fetch conversation detail from the backend
 */
export async function fetchConversationDetail(
  conversationId: string
): Promise<ConversationDetailResponse> {
  try {
    if (!conversationId) {
      throw new ConversationApiError(
        'Conversation ID is required',
        400,
        'MISSING_CONVERSATION_ID'
      );
    }

    // Build query parameters
    const searchParams = new URLSearchParams();
    searchParams.append('conversationID', conversationId);

    const endpoint = `/conversation?${searchParams.toString()}`;

    // Make API request - backend returns array with single item
    const response = await apiClient.get<ConversationDetailResponse[]>(endpoint);

    // Validate response
    if (!Array.isArray(response)) {
      throw new ConversationApiError(
        'Invalid response format: expected array',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    if (response.length === 0) {
      throw new ConversationApiError(
        'Conversation not found',
        404,
        'CONVERSATION_NOT_FOUND'
      );
    }

    const conversationData = response[0];

    // Validate conversation data
    if (!conversationData.conversationID || !conversationData.conversationHistory) {
      throw new ConversationApiError(
        'Invalid conversation data: missing required fields',
        undefined,
        'INVALID_CONVERSATION_DATA'
      );
    }

    return conversationData;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ConversationApiError(
      `Failed to fetch conversation detail: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'FETCH_DETAIL_FAILED'
    );
  }
}

/**
 * Query key factory for conversations
 * Provides consistent cache keys for TanStack Query
 */
export const conversationQueryKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationQueryKeys.all, 'list'] as const,
  list: (params: ConversationParams) => [
    ...conversationQueryKeys.lists(),
    params,
  ] as const,
  details: () => [...conversationQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationQueryKeys.details(), id] as const,
  // Rename operations
  renames: () => [...conversationQueryKeys.all, 'rename'] as const,
  rename: (id: string) => [...conversationQueryKeys.renames(), id] as const,
  verifications: () => [...conversationQueryKeys.all, 'verification'] as const,
  verification: (id: string) => [...conversationQueryKeys.verifications(), id] as const,
  // Delete operations
  deletes: () => [...conversationQueryKeys.all, 'delete'] as const,
  delete: (id: string) => [...conversationQueryKeys.deletes(), id] as const,
  batchDeletes: () => [...conversationQueryKeys.all, 'batch-delete'] as const,
};

/**
 * TanStack Query options for conversations
 * Provides reusable, type-safe query configuration
 */
export const conversationsQueryOptions = (params: ConversationParams = {}) =>
  queryOptions({
    queryKey: conversationQueryKeys.list(params),
    queryFn: () => fetchConversations(params),
    staleTime: 30 * 1000, // 30 seconds - more responsive updates
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
    retry: (failureCount: number, error: any) => {
      // Don't retry authentication errors
      if (error instanceof ApiError && error.status === 401) {
        return false;
      }

      // Don't retry client errors (4xx)
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
      }

      // Retry network errors and server errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Refetch stale data when window regains focus
    refetchOnMount: true, // Refetch when component mounts
    // Context7 pattern: Conservative background refetching - rely on invalidation
    refetchInterval: false, // No polling - use event-driven invalidation
    refetchIntervalInBackground: false,
  });

/**
 * TanStack Query options for conversation detail
 * Provides reusable, type-safe query configuration with performance optimizations
 */
export const conversationDetailQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: conversationQueryKeys.detail(conversationId),
    queryFn: () => fetchConversationDetail(conversationId),
    staleTime: 2 * 60 * 1000, // 2 minutes - reasonable freshness for conversations
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory longer for better UX
    retry: (failureCount: number, error: any) => {
      // Don't retry authentication errors
      if (error instanceof ApiError && error.status === 401) {
        return false;
      }

      // Don't retry not found errors
      if (error instanceof ApiError && error.status === 404) {
        return false;
      }

      // Don't retry client errors (4xx)
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
      }

      // Retry network errors and server errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Refetch stale data when window regains focus
    refetchOnMount: true, // Allow refetch on mount to show loading states during navigation
    refetchOnReconnect: true, // Refetch when connection is restored
    enabled: !!conversationId, // Only run query if conversationId is provided
    // Guard against a refetch racing the backend's async save: never let a
    // response with an empty history overwrite a cache that already has messages.
    structuralSharing: (oldData: unknown, newData: unknown) => {
      const old = oldData as ConversationDetailResponse | undefined;
      const next = newData as ConversationDetailResponse;
      if (old?.conversationHistory?.length && next?.conversationHistory?.length === 0) {
        return old;
      }
      return next;
    },
    // Performance optimization: prefetch related queries
    meta: {
      conversationId,
    },
  });

/**
 * Rename a conversation using the legacy API
 * Follows existing patterns from fetchConversations and maintains consistency
 */
export async function renameConversation(
  params: RenameConversationParams
): Promise<RenameConversationResponse> {
  try {
    const { conversationId, newTitle } = params;

    // Validate inputs
    if (!conversationId) {
      throw new ConversationApiError(
        'Conversation ID is required',
        400,
        'MISSING_CONVERSATION_ID'
      );
    }

    if (!newTitle.trim()) {
      throw new ConversationApiError(
        'New title is required',
        400,
        'MISSING_TITLE'
      );
    }

    // Prepare request payload following legacy format
    const payload: RenameConversationRequest = {
      userConversations: [
        {
          conversationID: conversationId,
          conversationTitle: newTitle.trim(),
          category: {}, // Maintain compatibility with legacy format
        },
      ],
    };

    // Make API request
    const response = await apiClient.patch<RenameConversationResponse>(
      '/rename_conversation',
      payload
    );

    // Validate response
    if (!response.updated_ids || !Array.isArray(response.updated_ids)) {
      throw new ConversationApiError(
        'Invalid response format: missing updated_ids',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    // Verify the conversation was actually updated
    if (!response.updated_ids.includes(conversationId)) {
      throw new ConversationApiError(
        'Conversation was not updated successfully',
        undefined,
        'UPDATE_FAILED'
      );
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ConversationApiError(
      `Failed to rename conversation: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'RENAME_FAILED'
    );
  }
}

/**
 * Delete multiple conversations using the legacy API
 * Direct implementation of legacy API supporting batch operations
 */
export async function deleteBatchConversations(
  params: BatchDeleteConversationParams
): Promise<DeleteConversationResponse> {
  try {
    const { conversationIds } = params;

    // Validate inputs
    if (!conversationIds || conversationIds.length === 0) {
      throw new ConversationApiError(
        'At least one conversation ID is required',
        400,
        'MISSING_CONVERSATION_IDS'
      );
    }

    // Validate each conversation ID
    conversationIds.forEach((id, index) => {
      if (!id || !id.trim()) {
        throw new ConversationApiError(
          `Invalid conversation ID at index ${index}`,
          400,
          'INVALID_CONVERSATION_ID'
        );
      }
    });

    // Prepare request payload following legacy format
    const payload: DeleteConversationRequest = {
      conversationID: conversationIds,
    };

    // Make API request
    const response = await apiClient.post<DeleteConversationResponse>(
      '/conversation_delete',
      payload
    );

    // Validate response structure
    if (!response.statistics || typeof response.statistics.total_time !== 'number') {
      throw new ConversationApiError(
        'Invalid response format: missing statistics',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    // Since API doesn't confirm deletion, we trust the request succeeded
    // Optimistic updates will handle the UI immediately
    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ConversationApiError(
      `Failed to delete conversations: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'DELETE_FAILED'
    );
  }
}

/**
 * Delete a single conversation
 * Wraps batch delete for consistency with backend API
 */
export async function deleteConversation(
  params: DeleteConversationParams
): Promise<DeleteConversationResponse> {
  return deleteBatchConversations({ conversationIds: [params.conversationId] });
}

/**
 * Create a branched conversation from an existing conversation history snapshot
 */
export async function branchConversation(
  params: BranchConversationParams
): Promise<BranchConversationResponse> {
  try {
    const {
      newConversationId,
      newConversationTitle,
      conversationHistory,
      profile,
      category,
      genie_conversation_ids,
      genie_space_names,
      genie_conversation_id,
      foundry_rag_conversation_ids,
      websearch_conversation_id,
    } = params;

    if (!newConversationId) {
      throw new ConversationApiError(
        'New conversation ID is required',
        400,
        'MISSING_CONVERSATION_ID'
      );
    }

    if (!newConversationTitle.trim()) {
      throw new ConversationApiError(
        'New conversation title is required',
        400,
        'MISSING_CONVERSATION_TITLE'
      );
    }

    if (!Array.isArray(conversationHistory) || conversationHistory.length === 0) {
      throw new ConversationApiError(
        'Conversation history is required to branch',
        400,
        'MISSING_CONVERSATION_HISTORY'
      );
    }

    const payload = {
      userConversations: [
        {
          conversationID: newConversationId,
          conversationTitle: newConversationTitle.trim(),
          category: category || {},
          ...(profile ? { profile } : {}),
        },
      ],
      conversationDetails: [
        {
          conversationID: newConversationId,
          conversationHistory,
          ...(genie_conversation_ids ? { genie_conversation_ids } : {}),
          ...(genie_space_names ? { genie_space_names } : {}),
          ...(genie_conversation_id ? { genie_conversation_id } : {}),
          ...(foundry_rag_conversation_ids ? { foundry_rag_conversation_ids } : {}),
          ...(websearch_conversation_id ? { websearch_conversation_id } : {}),
        },
      ],
      conversationThoughtProcess: [],
    };

    const response = await apiClient.post<BranchConversationResponse>(
      '/conversations',
      payload
    );

    if (!response.updated_ids || !Array.isArray(response.updated_ids)) {
      throw new ConversationApiError(
        'Invalid response format: missing updated_ids',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    if (!response.updated_ids.includes(newConversationId)) {
      throw new ConversationApiError(
        'Branch conversation was not saved successfully',
        undefined,
        'BRANCH_SAVE_FAILED'
      );
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ConversationApiError(
      `Failed to branch conversation: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'BRANCH_FAILED'
    );
  }
}

/**
 * Transform backend ChatMessage to frontend ConversationMessage format
 * Enhanced to handle message-level file associations and conversation level genie metadata enrichment
 */
export function transformChatMessage(
  chatMessage: ChatMessage,
  _index: number,
  conversationMetadata?: {
    genie_space_names?: string[];
    genie_conversation_id?: string;
  }
): any {

  // Determine agent information from tools
  const agentInfo = determineAgentInfo(chatMessage.tools, chatMessage.agent_classification);

  // Create user message with file associations
  const userMessage = {
    id: `${chatMessage.message_id}-user`,
    role: 'user' as const,
    content: chatMessage.user,
    timestamp: new Date(),

    // Include message-level file associations
    uploadedFiles: chatMessage.uploadedFiles || [],
    upload_session_id: chatMessage.upload_session_id,

    actions: {
      canEdit: true,
      canDelete: true,
      canRegenerate: false,
      canPlayAudio: false,
      isBookmarked: false,
      isLiked: false,
      isDisliked: false,
    },
    metadata: {
      originalMessageId: chatMessage.message_id,
      hasFiles: (chatMessage.uploadedFiles || []).length > 0,
    },
  };

  // Create assistant message from the bot content
  const assistantMessage = {
    id: `${chatMessage.message_id}-assistant`,
    role: 'assistant' as const,
    content: chatMessage.bot,
    digital_maintenance_status: chatMessage.digital_maintenance_status,
    toolCalls: chatMessage.toolCalls || [],
    timestamp: new Date(),

    // for historical message, the genie metadata exist at the message level, for new chats, it exists at the conversation level
    genie_space_names:
      normalizeGenieSpaceNames(
        chatMessage.genie_space_names,
        (chatMessage as { genie_space_name?: string }).genie_space_name
      ) || conversationMetadata?.genie_space_names,
    genie_conversation_id: chatMessage.genie_conversation_id || conversationMetadata?.genie_conversation_id,
    genie_message_id: chatMessage.genie_message_id,

    actions: {
      canEdit: false,
      canDelete: true,
      canRegenerate: true,
      canPlayAudio: true,
      isBookmarked: false,
      isLiked: false,
      isDisliked: false,
    },
    metadata: {
      agentInfo,
      originalMessageId: chatMessage.message_id,
      gptVersion: chatMessage.gptVersion,
      appVersion: chatMessage.appVersion,
      tools: chatMessage.tools,
      temperature: chatMessage.temperature,
      promptTemplate: chatMessage.promptTemplate,
      filterCategory: chatMessage.filterCategory,
      // Backward compat: carry thoughts & data_points from the done response
      // so the thought process panel can render without a separate API call.
      thoughts: (chatMessage as any).thoughts,
      dataPoints: (chatMessage as any).data_points,
      agent_classification: chatMessage.agent_classification,
      warning: {
        show: true,
        message: 'AI may make mistakes. Check important info.',
        type: 'info' as const,
      },
    },
  };

  return [userMessage, assistantMessage];
}

/**
 * Transform full conversation history to message array
 * Enriches messages with conversation level metadata for proper feedback submission
 */
export function transformConversationHistory(conversation: ConversationDetailResponse): any[] {
  const messages: any[] = [];

  // extract conversation level genie metadata to enrich messages
  const conversationMetadata = {
    genie_space_names: normalizeGenieSpaceNames(
      conversation.genie_space_names,
      (conversation as { genie_space_name?: string }).genie_space_name
    ),
    genie_conversation_id: conversation.genie_conversation_id,
  };

  // Handle the case where conversationHistory might be a single object or an array
  const conversationHistory = Array.isArray(conversation.conversationHistory)
    ? conversation.conversationHistory
    : [conversation.conversationHistory];


  conversationHistory.forEach((chatMessage, index) => {
    const transformedMessages = transformChatMessage(chatMessage, index, conversationMetadata);
    messages.push(...transformedMessages);
  });

  return messages;
}

/**
 * Utility to get all files from conversation messages
 */
export function getAllConversationFiles(conversation: ConversationDetailResponse): string[] {
  // Handle the case where conversationHistory might be a single object or an array
  const conversationHistory = Array.isArray(conversation.conversationHistory)
    ? conversation.conversationHistory
    : [conversation.conversationHistory];

  // Collect files from individual messages
  const messageFiles = conversationHistory
    .filter(msg => msg.uploadedFiles && msg.uploadedFiles.length > 0)
    .flatMap(msg => msg.uploadedFiles || []);

  return [...new Set(messageFiles)]; // Remove duplicates
}

/**
 * Determine agent information from tools array
 */
function determineAgentInfo(tools?: Array<{name: string}>, agent_classification?: string[]): any {
  if (agent_classification && agent_classification.length > 0) {
    // Map agent_classification to display name
    const mapping: Record<string, { id: string; name: string; type: string }> = {
      ppl: { id: 'ppl-agent', name: 'PPL Agent', type: 'ppl' },
      genie: { id: 'genie-agent', name: 'Genie Agent', type: 'genie' },
      llm: { id: 'generic-agent', name: 'Generic Agent', type: 'generic' },
      generic: { id: 'generic-agent', name: 'Generic Agent', type: 'generic' },
      safecoopfileagent: { id: 'safecoopfile-agent', name: 'File Agent', type: 'safecoopfile' },
      m365: { id: 'm365-agent', name: 'M365 Agent', type: 'm365' },
      websearch: { id: 'websearch-agent', name: 'WebSearch Agent', type: 'websearch' },
      image: { id: 'image-agent', name: 'Image Agent', type: 'image' },
    };
    // Use the first classification for display (or you can join multiple)
    // const key = agent_classification[0].toLowerCase();
    return agent_classification.map(key => mapping[key.toLowerCase()] || { id: 'unknown-agent', name: '@Unknown Agent', type: 'unknown' });
  }
}

/**
 * Utility functions for conversation data
 */
export const conversationUtils = {
  /**
   * Sort conversations by last accessed date (most recent first)
   */
  sortByLastAccessed: (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => {
      const dateA = new Date(a.date_last_accesed).getTime();
      const dateB = new Date(b.date_last_accesed).getTime();
      return dateB - dateA; // Descending order
    });
  },

  /**
   * Group conversations by date categories
   */
  groupByDate: (conversations: Conversation[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const groups = {
      pinned: [] as Conversation[], // TODO: Implement pinning logic
      today: [] as Conversation[],
      yesterday: [] as Conversation[],
      lastWeek: [] as Conversation[],
      older: [] as Conversation[],
    };

    const sortedConversations = conversationUtils.sortByLastAccessed(conversations);

    sortedConversations.forEach(conversation => {
      const accessDate = new Date(conversation.date_last_accesed);

      if (accessDate >= today) {
        groups.today.push(conversation);
      } else if (accessDate >= yesterday) {
        groups.yesterday.push(conversation);
      } else if (accessDate >= lastWeek) {
        groups.lastWeek.push(conversation);
      } else {
        groups.older.push(conversation);
      }
    });

    return groups;
  },

  /**
   * Filter conversations by search term
   */
  filterBySearch: (conversations: Conversation[], searchTerm: string): Conversation[] => {
    if (!searchTerm.trim()) {
      return conversations;
    }

    const term = searchTerm.toLowerCase();
    return conversations.filter(conversation =>
      conversation.conversationTitle.toLowerCase().includes(term) ||
      conversation.profile.toLowerCase().includes(term)
    );
  },

  /**
   * Get display title for conversation (truncated if needed)
   */
  getDisplayTitle: (conversation: Conversation, maxLength = 200): string => {
    const title = conversation.conversationTitle.trim();
    if (title.length <= maxLength) {
      return title;
    }
    return title.substring(0, maxLength - 3) + '...';
  },
};


export async function updateConversationDateLastAccessed(conversationId: string): Promise<void> {
  /**
   * Update the last accessed date for a conversation
   * @param conversationId - ID of the conversation to update
   */

    if (!conversationId) {
      throw new Error('Conversation ID is required to update last accessed date');
    }

    const payload: UpdateConversationRequest = {
      conversationID: conversationId,
    };


    try {
      // Make API request
      await apiClient.patch<UpdateConversationResponse>(
        '/update_last_accessed',
        payload
      );
    } catch (error) {
      console.error(`Failed to update last accessed date for conversation ${conversationId}:`, error);
      throw new Error('Failed to update last accessed date');
    }
};
